import React, { useState } from 'react';
import LegalLayout, { LegalSection } from '../components/LegalLayout';
import { Mail, Shield, FileDown, Trash2, RefreshCw, EyeOff, ChevronRight } from 'lucide-react';

const rights = [
  {
    icon: Mail,
    title: "Droit d'accès",
    desc: "Obtenir une copie de toutes les données personnelles que nous détenons vous concernant.",
    color: "#ff6b6b",
  },
  {
    icon: RefreshCw,
    title: "Droit de rectification",
    desc: "Corriger des informations inexactes ou incomplètes vous concernant.",
    color: "#ffd93d",
  },
  {
    icon: Trash2,
    title: 'Droit à l\'effacement ("droit à l\'oubli")',
    desc: "Demander la suppression de vos données personnelles, sous réserve d'obligations légales.",
    color: "#06d6a0",
  },
  {
    icon: FileDown,
    title: "Droit à la portabilité",
    desc: "Recevoir vos données dans un format structuré et lisible par machine (JSON).",
    color: "#a855f7",
  },
  {
    icon: EyeOff,
    title: "Droit d'opposition",
    desc: "Vous opposer au traitement de vos données pour des raisons tenant à votre situation particulière.",
    color: "#f97316",
  },
  {
    icon: Shield,
    title: "Droit à la limitation",
    desc: "Demander la suspension temporaire du traitement de vos données dans certains cas.",
    color: "#3b82f6",
  },
];

export default function DonneesPersonnelles() {
  const [openRight, setOpenRight] = useState(null);

  return (
    <LegalLayout title="Vos données personnelles" lastUpdated="1er janvier 2025">
      <p className="text-[#a0a0ab]">
        Kurso respecte vos droits en matière de données personnelles et s'engage à répondre à toute demande dans les meilleurs délais. Cette page vous guide pour exercer vos droits RGPD.
      </p>

      <LegalSection title="1. Vos droits RGPD">
        <div className="grid gap-3">
          {rights.map((right) => (
            <button
              key={right.title}
              onClick={() => setOpenRight(openRight === right.title ? null : right.title)}
              className="text-left bg-white/5 hover:bg-white/8 border border-white/10 rounded-xl p-3 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: right.color + '20' }}
                >
                  <right.icon size={16} style={{ color: right.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs font-medium">{right.title}</p>
                  {openRight === right.title && (
                    <p className="text-[#a0a0ab] text-xs mt-1">{right.desc}</p>
                  )}
                </div>
                <ChevronRight
                  size={14}
                  className={`text-[#a0a0ab] transition-transform ${openRight === right.title ? 'rotate-90' : ''}`}
                />
              </div>
            </button>
          ))}
        </div>
      </LegalSection>

      <LegalSection title="2. Comment exercer vos droits">
        <p>
          Pour exercer l'un de vos droits, vous pouvez :</p>
        <div className="space-y-2 mt-2">
          <div className="flex items-start gap-3 bg-white/5 rounded-xl p-3">
            <div className="w-6 h-6 rounded-full bg-[#ff6b6b]/20 text-[#ff6b6b] flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
            <div>
              <p className="text-white text-xs font-medium">Par email</p>
              <p className="text-[#a0a0ab] text-xs">Envoyez votre demande à <a href="mailto:dpo@mykurso.fr" className="text-[#ff6b6b] hover:underline">dpo@mykurso.fr</a> en précisant votre nom, email de compte et le droit que vous souhaitez exercer.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-white/5 rounded-xl p-3">
            <div className="w-6 h-6 rounded-full bg-[#ff6b6b]/20 text-[#ff6b6b] flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
            <div>
              <p className="text-white text-xs font-medium">Depuis votre profil</p>
              <p className="text-[#a0a0ab] text-xs">Certains droits (rectification du prénom, suppression du compte) sont directement accessibles depuis vos paramètres de profil.</p>
            </div>
          </div>
          <div className="flex items-start gap-3 bg-white/5 rounded-xl p-3">
            <div className="w-6 h-6 rounded-full bg-[#ff6b6b]/20 text-[#ff6b6b] flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
            <div>
              <p className="text-white text-xs font-medium">Justification d'identité</p>
              <p className="text-[#a0a0ab] text-xs">Nous pouvons vous demander une preuve d'identité (copie de pièce d'identité) pour protéger votre compte contre les demandes frauduleuses.</p>
            </div>
          </div>
        </div>
      </LegalSection>

      <LegalSection title="3. Délai de réponse">
        <p>
          Nous nous engageons à répondre à toute demande dans un délai de <strong>30 jours</strong> à compter de sa réception. En cas de demande complexe ou de grande quantité, ce délai peut être prolongé de 2 mois supplémentaires, avec notification préalable.
        </p>
      </LegalSection>

      <LegalSection title="4. Droit à l'effacement — Procédure">
        <p>
          Si vous souhaitez supprimer votre compte et toutes vos données personnelles :
        </p>
        <ul className="list-disc pl-5 space-y-1 text-[#a0a0ab] mt-2">
          <li>Envoyez un email à <a href="mailto:dpo@mykurso.fr" className="text-[#ff6b6b] hover:underline">dpo@mykurso.fr</a> avec l'objet "Demande d'effacement"</li>
          <li>Votre compte sera suspendu immédiatement et définitivement supprimé dans les <strong>30 jours</strong></li>
          <li>Exception : les données de facturation sont conservées 10 ans (obligation légale)</li>
          <li>Les contenus partagés dans des espaces collectifs peuvent être anonymisés plutôt que supprimés</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Droit à la portabilité — Export de données">
        <p>
          Vous pouvez demander l'export de toutes vos données personnelles dans un format JSON structuré, incluant :
        </p>
        <ul className="list-disc pl-5 space-y-1 text-[#a0a0ab] mt-2">
          <li>Informations de compte (nom, email, date d'inscription)</li>
          <li>Tous vos cours générés et leur progression</li>
          <li>Historique de quiz et scores</li>
          <li>Résultats du Bilan Kurso</li>
          <li>XP, badges et série quotidienne</li>
        </ul>
        <p className="mt-2">
          Envoyez une demande à <a href="mailto:dpo@mykurso.fr" className="text-[#ff6b6b] hover:underline">dpo@mykurso.fr</a> avec l'objet "Export de mes données".
        </p>
      </LegalSection>

      <LegalSection title="6. Réclamation auprès de la CNIL">
        <p>
          Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous avez la possibilité d'introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
        </p>
        <div className="mt-2 bg-white/5 rounded-xl p-3 text-xs">
          <p className="text-white font-medium">CNIL</p>
          <p className="text-[#a0a0ab]">3 Place de Fontenoy — 75007 Paris</p>
          <p className="text-[#a0a0ab]">
            <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noopener noreferrer" className="text-[#ff6b6b] hover:underline">
              www.cnil.fr/fr/plaintes
            </a>
          </p>
        </div>
      </LegalSection>

      <LegalSection title="7. Contact DPO">
        <p>
          Pour toute question relative à vos données personnelles, contactez notre Délégué à la Protection des Données :
        </p>
        <div className="mt-2 bg-white/5 rounded-xl p-3 text-xs">
          <p className="text-white font-medium">Délégué à la Protection des Données — Kurso</p>
          <p className="text-[#a0a0ab]">
            Email : <a href="mailto:dpo@mykurso.fr" className="text-[#ff6b6b] hover:underline">dpo@mykurso.fr</a>
          </p>
          <p className="text-[#a0a0ab]">Délai de réponse : 30 jours ouvrés</p>
        </div>
      </LegalSection>
    </LegalLayout>
  );
}
